import { useCallback, useRef } from 'react'
import { Col, Row } from 'react-grid-system'
import { NormalFormEmailField, NormalFormPhoneField, NormalFormTextField } from '../molecules'
import { useUserLanguage } from '@/customHooks'
import textsData from '@/textData/FormModalsTexts.json'
import { CreateInsuranceInput, InsuranceOutput, UpdateInsuranceInput } from '@/graphql/generated'

export type InsuranceFormData = {
  id?: string
  insuranceCompany: string
  insuranceHolder: string
  holderDocument: string
  insurancePhone: string
  policyNumber: string
  insuranceBroker: string
  insuranceBrokerPhone: string
  insuranceBrokerEmail: string
  policyPlan: string
}


export const toCreateInsuranceInput = (data: InsuranceFormData): CreateInsuranceInput => {
  return {
    insuranceCompany: data.insuranceCompany || '',
    insurancePhone: data.insurancePhone || '',
    policyNumber: data.policyNumber || '',
    insuranceHolder: data.insuranceHolder || undefined,
    holderDocument: data.holderDocument || undefined,
    insuranceBroker: data.insuranceBroker || undefined,
    insuranceBrokerPhone: data.insuranceBrokerPhone || undefined,
    insuranceBrokerEmail: data.insuranceBrokerEmail || undefined,
    policyPlan: data.policyPlan || undefined,
  }
}


export const toUpdateInsuranceInput = (data: InsuranceFormData): UpdateInsuranceInput => {
  return {
    insuranceCompany: data.insuranceCompany || undefined,
    insurancePhone: data.insurancePhone || undefined,
    policyNumber: data.policyNumber || undefined,
    insuranceHolder: data.insuranceHolder || undefined,
    holderDocument: data.holderDocument || undefined,
    insuranceBroker: data.insuranceBroker || undefined,
    insuranceBrokerPhone: data.insuranceBrokerPhone || undefined,
    insuranceBrokerEmail: data.insuranceBrokerEmail || undefined,
    policyPlan: data.policyPlan || undefined,
  }
}


export const fromInsuranceOutput = (insurance: InsuranceOutput): InsuranceFormData => {
  return {
    id: insurance.id,
    insuranceCompany: insurance.insuranceCompany ?? '',
    insurancePhone: insurance.insurancePhone ?? '',
    policyNumber: insurance.policyNumber ?? '',
    insuranceHolder: insurance.insuranceHolder ?? '',
    holderDocument: insurance.holderDocument ?? '',
    insuranceBroker: insurance.insuranceBroker ?? '',
    insuranceBrokerPhone: insurance.insuranceBrokerPhone ?? '',
    insuranceBrokerEmail: insurance.insuranceBrokerEmail ?? '',
    policyPlan: insurance.policyPlan ?? '',
  }
}

type AddInsuranceFormControlledProps = {
  onChange: (data: InsuranceFormData) => void
  defaultData?: InsuranceFormData
}

const emptyData: InsuranceFormData = {
  insuranceCompany: '',
  insuranceHolder: '',
  holderDocument: '',
  insurancePhone: '',
  policyNumber: '',
  insuranceBroker: '',
  insuranceBrokerPhone: '',
  insuranceBrokerEmail: '',
  policyPlan: '',
}

export const AddInsuranceFormControlled = ({ onChange, defaultData }: AddInsuranceFormControlledProps) => {
  const userLanguage = useUserLanguage()
  const texts = userLanguage === 'es' ? textsData.es.insuranceForm : textsData.en.insuranceForm

  const rentDataRef = useRef<InsuranceFormData>(defaultData ?? emptyData)

  const onChangeHandle = useCallback((field: keyof InsuranceFormData, value: string | null) => {
    rentDataRef.current = {
      ...rentDataRef.current,
      [field]: value ?? '',
    }
    onChange(rentDataRef.current)
  }, [])

  return (
    <>
      <Row align='center'>
        <Col>
          <NormalFormTextField
            label={texts.insuranceCompany.label}
            placeholder={texts.insuranceCompany.placeholder}
            name='insuranceCompany'
            maxLength={200}
            onChange={(value) => onChangeHandle('insuranceCompany', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.insuranceHolder.label}
            placeholder={texts.insuranceHolder.placeholder}
            name="insuranceHolder"
            maxLength={200}
            onChange={(value) => onChangeHandle('insuranceHolder', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            label={texts.holderDocument.label}
            placeholder={texts.holderDocument.placeholder}
            name="holderDocument"
            maxLength={20}
            onChange={(value) => onChangeHandle('holderDocument', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormPhoneField
            name="insurancePhone"
            label={texts.insurancePhone.label}
            onChange={(value) => onChangeHandle('insurancePhone', value)}
            required
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            name="insurancePolicyNumber"
            label={texts.policyNumber.label}
            placeholder={texts.policyNumber.placeholder}
            maxLength={100}
            onChange={(value) => onChangeHandle('policyNumber', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            name="insuranceBroker"
            label={texts.insuranceBroker.label}
            placeholder={texts.insuranceBroker.placeholder}
            maxLength={200}
            onChange={(value) => onChangeHandle('insuranceBroker', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormPhoneField
            name="insuranceBrokerPhone"
            label={texts.insuranceBrokerPhone.label}
            onChange={(value) => onChangeHandle('insuranceBrokerPhone', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormEmailField
            label={texts.insuranceBrokerEmail.label}
            placeholder={texts.insuranceBrokerEmail.placeholder}
            name='insuranceBrokerEmail'
            maxLength={200}
            onChange={(value) => onChangeHandle('insuranceBrokerEmail', value)}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <NormalFormTextField
            name="policyPlan"
            label={texts.policyPlan.label}
            placeholder={texts.policyPlan.placeholder}
            maxLength={200}
            onChange={(value) => onChangeHandle('policyPlan', value)}
          />
        </Col>
      </Row>
    </>
  )
}